import React, { useEffect, useState } from "react";
import "./profile.scss";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import UpdateProfile from "./UpdateProfile";
import PersonalProfile from "./PersonalProfile";
import EducationalInfo from "./EducationalInfo";
import { useMediaQuery } from "@mui/material";

import Skills from "./Skills";
import Resume from "./Resume";
import { CandidateProfile } from "../../../Redux/action/CandidateProfileAction";
import video from "../../../images/video_img.jpg";
import Pricing from "./Pricing";
import Certifications from "./Certifications";
import Experience from "./Experience";
import Swal from "sweetalert2";
import axios from "axios";

import {
  Box,
  Card,
  Tabs,
  Tab,
  Typography,
  Divider,
  Stack,
  Button,
} from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import SchoolIcon from "@mui/icons-material/School";
import WorkIcon from "@mui/icons-material/Work";
import VerifiedIcon from "@mui/icons-material/Verified";
import StarIcon from "@mui/icons-material/Star";
import DescriptionIcon from "@mui/icons-material/Description";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import CardTravelIcon from "@mui/icons-material/CardTravel";
import VisaInformation from "./VisaInformation";
import TravelHistory from "./TravelHistory";
import { History } from "@mui/icons-material";

const Profile = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  const userInfo = useSelector((state) => state.Profile.result);
  const { userResult } = useSelector((state) => state.Auth);
  const [activeTab, setActiveTab] = useState("Profile");
  const [openSkills, setOpenSkills] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const candidateId = userResult?.userid;
  const isTeamMember = userInfo?.vendorId;
  const dispatch = useDispatch();
  const baseUrl = process.env.REACT_APP_API_PATH;
  const userVideo = useSelector((state) => state?.Profile?.result?.videoUrl);

  // ---- TABS ----
  let tabs;
  if (isTeamMember === null && parseInt(userInfo?.keyExperience) >= 1) {
    tabs = [
      "Profile",
      "Contact Info",
      "Educational",
      "Experience",
      "Certifications",
      "Skills & Ratings",
      "Resume",
      "Pricing",
      "Visa Information",
      "Travel History",
    ];
  } else {
    tabs = [
      "Profile",
      "Contact Info",
      "Educational",
      "Experience",
      "Certifications",
      "Skills & Ratings",
      "Resume",
      "Visa Information",
      "Travel History",
    ];
  }

  const tabIcons = {
    Profile: <PersonIcon />,
    "Contact Info": <ContactPhoneIcon />,
    Educational: <SchoolIcon />,
    Experience: <WorkIcon />,
    Certifications: <VerifiedIcon />,
    "Skills & Ratings": <StarIcon />,
    Resume: <DescriptionIcon />,
    Pricing: <MonetizationOnIcon />,
    "Visa Information": <CardTravelIcon />,
    "Travel History": <History/>,
  };

  const tabComponents = {
    Profile: (
      <UpdateProfile openSkills={openSkills} setOpenSkills={setOpenSkills} />
    ),
    "Contact Info": <PersonalProfile />,
    "Visa Information": <VisaInformation candidateId={candidateId} />,
    "Travel History": <TravelHistory candidateId={candidateId}/>,
    Educational: <EducationalInfo />,
    Experience: <Experience />,
    Certifications: <Certifications />,
    "Skills & Ratings": (
      <Skills
        openSkills={openSkills}
        setOpenSkills={setOpenSkills}
        setActiveTab={setActiveTab}
      />
    ),
    Resume: <Resume />,
    ...(isTeamMember === null &&
      parseInt(userInfo?.keyExperience) >= 1 && { Pricing: <Pricing /> }),
  };

  // ---- DATA LOADING ----
  useEffect(() => {
    dispatch(CandidateProfile(candidateId));
  }, []);

  useEffect(() => {
    dispatch(CandidateProfile(candidateId));
  }, [selectedVideo]);

  // ---- VIDEO UPLOAD ----
  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const MAX_SIZE_MB = 20;

    if (file?.type?.split("/")[0] !== "video") {
      return toast.error("Only video files accepted");
    }

    if (file.size / (1024 * 1024) > MAX_SIZE_MB) {
      toast.error(`File must be under ${MAX_SIZE_MB}MB`);
      return;
    }
    setSelectedVideo(file);
  };

  useEffect(() => {
    if (!selectedVideo) return;

    const formData = new FormData();
    formData.append("file", selectedVideo);
    formData.append("candidateId", candidateId);

    fetch(`${baseUrl}/ebench/upload_video`, { method: "POST", body: formData })
      .then(() => {
        toast.success("Video Uploaded Successfully");
        setTimeout(() => dispatch(CandidateProfile(candidateId)), 900);
      })
      .catch(() => toast.error("Upload failed"));
  }, [selectedVideo]);

  // ---- DELETE VIDEO ----
  const handleVideoDelete = () => {
    Swal.fire({
      title: "Delete Video?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d32f2f",
    }).then(async (res) => {
      if (res.isConfirmed) {
        await axios.delete(
          `${baseUrl}/ebench/delete_resume_or_video?candidateId=${candidateId}&deleteVideo=true`
        );
        toast.success("Deleted Successfully");
        setTimeout(() => dispatch(CandidateProfile(candidateId)), 900);
      }
    });
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexWrap: { xs: "wrap", md: "nowrap" }, // responsive
          gap: 3,
          mt: 3,
          px: { xs: 1, md: 3 },
        }}
      >
        {/* ---------- LEFT SIDEBAR ---------- */}
        <Card
          sx={{
            width: { xs: "100%", md: "260px" },
            minWidth: { md: "260px" },
            height: { xs: "auto", md: "85vh" },
            p: 2,
            borderRadius: 3,
            position: { md: "sticky" },
            top: "90px",
            overflowY: "auto",
            boxShadow: "0 3px 12px rgba(0,0,0,0.1)",
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ color: "#286a94", mb: 2 }}
          >
            My Profile
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Tabs
            orientation={isMobile ? "horizontal" : "vertical"}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons={false}
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            sx={{
              width: "100%",
              display: "flex",
              flexWrap: isMobile ? "wrap" : "nowrap",

              ".MuiTabs-flexContainer": {
                flexWrap: isMobile ? "wrap" : "nowrap",
              },

              ".MuiTab-root": {
                width: isMobile ? "48%" : "100%",
                justifyContent: "flex-start",
                textTransform: "none",
                borderRadius: 2,
                minHeight: "40px",
                borderBottom: isMobile ? "none" : "1px solid #e0e0e0",
                whiteSpace: "nowrap",
                "&:hover": { background: "#f5f8fa" },
              },

              ".Mui-selected": {
                backgroundColor: "#e9f3ff",
                fontWeight: "bold",
                color: "#286a94 !important",
              },

              ".MuiTabs-indicator": {
                backgroundColor: "#286a94",
                width: isMobile ? "100%" : "3px",
                height: isMobile ? "3px" : "100%",
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab}
                value={tab}
                icon={tabIcons[tab]}
                iconPosition="start"
                label={tab}
              />
            ))}
          </Tabs>
        </Card>

        {/* ---------- MAIN CONTENT ---------- */}
        <Card
          sx={{
            flexGrow: 1,
            p: { xs: 1, md: 3 },
            width: { xs: "100%", md: "auto" },
            minHeight: "85vh",
            whiteSpace: "nowrap",
            borderRadius: 3,
          }}
        >
          {tabComponents[activeTab]}
        </Card>

        {/* ---------- VIDEO PANEL ---------- */}
        <Card
          sx={{
            width: { xs: "100%", md: "300px" },
            minWidth: { md: "300px" },
            p: 3,
            height: { xs: "auto", md: "85vh" },
            borderRadius: 3,
            overflowY: "auto",
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={1}>
            Notes & Video
          </Typography>

          <Divider />

          <Box mt={2}>
            {userVideo || selectedVideo ? (
              <video width="100%" controls style={{ borderRadius: 8 }}>
                <source
                  src={
                    selectedVideo
                      ? URL.createObjectURL(selectedVideo)
                      : userVideo
                  }
                  type="video/mp4"
                />
              </video>
            ) : (
              <img
                src={video}
                width="100%"
                style={{ borderRadius: 8 }}
                alt="preview"
              />
            )}
          </Box>

          <Stack direction="row" spacing={2} mt={3} justifyContent="center">
            <Button variant="outlined" component="label">
              Upload
              <input
                type="file"
                hidden
                accept="video/*"
                onChange={handleVideoFileChange}
              />
            </Button>

            {userVideo && (
              <Button
                color="error"
                variant="contained"
                onClick={handleVideoDelete}
              >
                Delete
              </Button>
            )}
          </Stack>
        </Card>
      </Box>
    </>
  );
};

export default Profile;
